import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { CompBadge } from "@/components/OpportunityCard";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2, ArrowLeft, Pencil, ShieldCheck, AlertTriangle, MapPin, Users,
  Calendar, CheckCircle2, Info,
} from "lucide-react";
import { toast } from "sonner";

const ATTESTATION = "I confirm that the information in this opportunity is accurate and I approve it for publication on Nearfluence.";

function Row({ label, value }) {
  if (value === undefined || value === null || value === "" ) return null;
  return (
    <div className="flex flex-col gap-0.5 border-b border-zinc-100 py-2.5 last:border-0 sm:flex-row sm:justify-between sm:gap-4">
      <span className="text-sm font-medium text-zinc-500">{label}</span>
      <span className="text-sm text-zinc-900 sm:text-right">{value}</span>
    </div>
  );
}

export default function OpportunityApprove() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/opportunities/${id}`).then((r) => setO(r.data)).catch(() => setO(false)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  const approve = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.post(`/opportunities/${id}/approve`, { attestation: true });
      toast.success("Approved — your opportunity is now live 🎉");
      navigate(`/business/opportunities/${id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not publish. Please try again.");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!o) return (
    <div className="min-h-screen bg-[#FAF9F6]"><Navbar />
      <div className="mx-auto max-w-2xl px-4 py-16 text-center text-zinc-500">This opportunity could not be found. <button onClick={() => navigate("/business/opportunities")} className="font-semibold text-emerald-700 underline">Back to your opportunities</button></div>
      <MobileNav />
    </div>
  );

  const alreadyLive = o.approval_state !== "awaiting_business_approval";
  const isEvent = o.opp_type === "event";
  const comp = o.compensation_type === "gifted"
    ? (o.gift_description || "Gifted")
    : o.compensation_type === "paid_gifted"
      ? `£${o.payment_amount ?? "—"} + ${o.gift_description || "gift"}`
      : (o.payment_amount != null ? `£${o.payment_amount}` : "Paid");

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-action-10">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 lg:px-8">
        <button onClick={() => navigate("/business/opportunities")} className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" /> Your opportunities</button>

        <div className="rounded-3xl border border-zinc-200 bg-white p-5" data-testid="approve-header">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-600 text-white"><ShieldCheck className="h-5 w-5" /></div>
            <div>
              <h1 className="font-display text-xl font-bold text-zinc-900">Review &amp; approve</h1>
              <p className="text-sm text-zinc-500">Nearfluence prepared this opportunity for your business. Check the details, then approve it to go live.</p>
            </div>
          </div>
        </div>

        {alreadyLive ? (
          <div data-testid="approve-already" className="mt-4 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">This opportunity has already been approved and is {o.status === "published" ? "live" : o.status}.</p>
              <Button size="sm" onClick={() => navigate(`/business/opportunities/${id}`)} className="mt-3 rounded-full bg-emerald-600 hover:bg-emerald-700">Manage applicants</Button>
            </div>
          </div>
        ) : (
          <>
            {o.cover_image && (
              <div className="mt-4 overflow-hidden rounded-3xl border border-zinc-200 bg-white">
                <img src={fileUrl(o.cover_image)} alt="" className="h-44 w-full object-cover" />
              </div>
            )}

            <div className="mt-4 rounded-3xl border border-zinc-200 bg-white p-5" data-testid="approve-summary">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-display text-lg font-bold text-zinc-900">{o.title || "Untitled opportunity"}</h2>
                {isEvent && <span className="rounded-full bg-fuchsia-600 px-2 py-0.5 text-xs font-bold text-white">EVENT</span>}
                <CompBadge type={o.compensation_type} />
                <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-xs font-semibold text-zinc-700">Draft — awaiting your approval</span>
              </div>
              <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-zinc-500">
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {o.location || "—"}</span>
                <span>{o.category || "—"}</span>
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {o.creators_needed} needed</span>
              </p>
              {o.description && <p className="mt-3 whitespace-pre-line text-sm text-zinc-700">{o.description}</p>}

              <div className="mt-3">
                <Row label="Compensation" value={comp} />
                <Row label="Visibility" value={o.visibility === "direct" ? "Private — invite only (won't show in the public list)" : o.visibility === "both" ? "Public + direct invites" : "Public"} />
                <Row label="Platforms" value={(o.platforms || []).join(", ")} />
                <Row label="Categories" value={(o.niches || []).join(", ")} />
                <Row label="Deliverables" value={o.deliverables} />
                <Row label="Minimum followers" value={o.min_followers ? o.min_followers.toLocaleString() : null} />
                <Row label="Application deadline" value={o.deadline} />
                <Row label="Campaign date" value={o.campaign_date} />
                <Row label="Content deadline" value={o.content_deadline} />
                {isEvent && <Row label="Venue" value={o.venue_name} />}
                {isEvent && <Row label="Event date" value={[o.event_date, o.start_time, o.end_time].filter(Boolean).join(" · ")} />}
                {isEvent && <Row label="What creators receive" value={o.what_creators_receive} />}
                <Row label="Additional requirements" value={o.additional_requirements} />
              </div>

              <Button variant="outline" data-testid="approve-edit-btn" onClick={() => navigate(`/business/opportunities/${id}/edit`)} className="mt-4 rounded-full">
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit details
              </Button>
            </div>

            {/* Higher-risk reminder — no extra legal checkboxes */}
            <div data-testid="approve-terms-reminder" className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-800">
                <b>Important:</b> By publishing this opportunity, you confirm that your business will comply with the responsibilities set out in the{" "}
                <Link to="/terms" target="_blank" className="font-semibold text-amber-900 underline">Nearfluence Terms of Service</Link>.
              </p>
            </div>

            {/* Attestation */}
            <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-2xl border border-zinc-200 bg-white p-4">
              <Checkbox data-testid="approve-attestation-checkbox" checked={confirm} onCheckedChange={(v) => setConfirm(!!v)} className="mt-0.5" />
              <span className="text-sm text-zinc-700">{ATTESTATION}</span>
            </label>

            <div className="mt-3 flex items-center gap-2 text-xs text-zinc-400">
              <Info className="h-3.5 w-3.5" /> It won’t be visible to creators or open for applications until you approve it.
            </div>
          </>
        )}
      </div>

      {!alreadyLive && (
        <div className="nf-actionbar-abovenav border-t border-zinc-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
            <span className="text-xs text-zinc-500">{confirm ? "Ready to publish" : "Tick the box to enable publishing"}</span>
            <Button data-testid="approve-publish-btn" onClick={approve} disabled={!confirm || busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="mr-1.5 h-4 w-4" /> Approve &amp; Publish</>}
            </Button>
          </div>
        </div>
      )}
      <MobileNav />
    </div>
  );
}
